import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/90 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <span className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
            Itaobuy<span className="text-accent-500">Spreadsheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to="/"
            className="px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Home
          </Link>

          <Link
            to="/articolo/guida-all-acquisto-itaobuy-spreadsheet"
            className="px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Guida all&apos;Acquisto
          </Link>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>Categorie</span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                      <i className={`${cat.icon} text-base`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="ml-3 px-5 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Vai allo Spreadsheet
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-background-100 border border-background-200 text-foreground-700 cursor-pointer"
          aria-label="Menu"
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-6 space-y-2">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-6 h-6 flex items-center justify-center text-accent-500">
                <i className="ri-home-line text-lg"></i>
              </span>
              Home
            </Link>

            <Link
              to="/articolo/guida-all-acquisto-itaobuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-6 h-6 flex items-center justify-center text-accent-500">
                <i className="ri-shopping-cart-line text-lg"></i>
              </span>
              Guida all&apos;Acquisto
            </Link>

            <Link
              to="/articolo/guida-completa-itaobuy-spreadsheet"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="w-6 h-6 flex items-center justify-center text-accent-500">
                <i className="ri-book-open-line text-lg"></i>
              </span>
              Guida Completa
            </Link>

            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-6 h-6 flex items-center justify-center text-accent-500">
                  <i className="ri-grid-line text-lg"></i>
                </span>
                Categorie
              </span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="pl-12 pr-4 space-y-1 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => { setMobileMenuOpen(false); setMobileCatOpen(false); }}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="pt-4 px-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold text-center hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Vai allo Spreadsheet
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>

            <div className="px-4 pt-6 border-t border-background-200 mt-4">
              <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Altri Articoli</p>
              <div className="space-y-2">
                <Link to="/articolo/itaobuy-spreadsheet-sicuro" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer">Sicurezza</Link>
                <Link to="/articolo/come-usare-itaobuy-spreadsheet" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer">Tutorial</Link>
                <Link to="/articolo/migliori-prodotti-itaobuy-spreadsheet-2026" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer">Migliori Prodotti</Link>
                <Link to="/articolo/itaobuy-spreadsheet-categorie-guida" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer">Categorie</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}